import { useQuery } from '@tanstack/react-query';
import { api, downloadFile } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card, Badge, Button, severityTone } from '../../components/ui.js';
import { QueryState } from '../../components/form.js';

interface Alert { id: string; severity: string; messageKey: string; actionKey: string; context: Record<string, string> }
interface Occurrence { examType: string; dueDate: string; status: string }
interface PatientResp {
  patient: { name: string; nickname: string | null; diagnosis: string | null; diagnosisStatus: string | null; treatmentStartDate: string | null } | null;
  baseline: { g6pdStatus: string } | null;
}

const M = {
  schedule: { fr: 'À faire (calendrier)', en: 'To do (schedule)' },
  about: { fr: 'À propos de mon enfant', en: 'About my child' },
  diagnosis: { fr: 'Diagnostic', en: 'Diagnosis' },
  treatmentStart: { fr: 'Début du traitement', en: 'Treatment start' },
  g6pd: { fr: 'Statut G6PD', en: 'G6PD status' },
  g6pdHint: { fr: 'Important pour la sécurité de certains médicaments — en parler au médecin.', en: 'Matters for the safety of some medicines — ask the doctor.' },
} as const;

export function ParentMore() {
  const { t, locale } = useI18n();
  const tr = (m: { fr: string; en: string }) => m[locale === 'en' ? 'en' : 'fr'];
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: () => api.get<{ alerts: Alert[] }>('/alerts') });
  const surveillance = useQuery({ queryKey: ['surveillance'], queryFn: () => api.get<{ occurrences: Occurrence[] }>('/surveillance') });
  const patientQ = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResp>('/patient') });
  const p = patientQ.data?.patient;
  const baseline = patientQ.data?.baseline;

  const upcoming = (surveillance.data?.occurrences ?? [])
    .filter((o) => o.status === 'due' || o.status === 'overdue' || o.status === 'upcoming')
    .slice(0, 8);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">{t('role.caregiver')}</h1>

      {/* About my child — diagnosis, treatment start, G6PD (read-only context). */}
      {p && (
        <Card title={tr(M.about)}>
          <dl className="text-sm grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
            <div>
              <dt className="text-slate-500">{tr(M.diagnosis)}</dt>
              <dd className="text-slate-800">
                {p.diagnosis ?? '—'}
                {p.diagnosisStatus ? <span className="text-slate-400"> · {p.diagnosisStatus}</span> : null}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">{tr(M.treatmentStart)}</dt>
              <dd className="text-slate-800">{p.treatmentStartDate ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{tr(M.g6pd)}</dt>
              <dd className="text-slate-800">{t(`g6pd.${baseline?.g6pdStatus ?? 'unknown'}`)}</dd>
            </div>
          </dl>
          <p className="text-xs text-slate-400 mt-2">{tr(M.g6pdHint)}</p>
        </Card>
      )}

      {/* Parent-relevant flags only (the API already hides doctor-only items). */}
      <Card title={t('severity.warning')}>
        <QueryState isLoading={alerts.isLoading} isError={alerts.isError} error={alerts.error} isEmpty={(alerts.data?.alerts.length ?? 0) === 0} emptyMessage="—" errorFallback={t('common.error')}>
          <ul className="space-y-2">
            {alerts.data?.alerts.map((a) => (
              <li key={a.id} className="text-sm flex items-start gap-2">
                <Badge tone={severityTone(a.severity)}>{t(`severity.${a.severity}`)}</Badge>
                <span>
                  {t(a.messageKey, a.context)} <span className="text-slate-500">— {t(a.actionKey)}</span>
                </span>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>

      <Card title={tr(M.schedule)}>
        <QueryState isLoading={surveillance.isLoading} isError={surveillance.isError} error={surveillance.error} isEmpty={upcoming.length === 0} emptyMessage="—" errorFallback={t('common.error')}>
          <ul className="space-y-1 text-sm">
            {upcoming.map((o, i) => (
              <li key={`${o.examType}-${o.dueDate}-${i}`} className="flex items-center justify-between">
                <span>{o.dueDate} — {t(`exam.${o.examType}`)}</span>
                <Badge tone={o.status === 'overdue' ? 'danger' : o.status === 'due' ? 'warn' : 'info'}>{t(`surv.${o.status}`)}</Badge>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>

      <Card title={t('app.tagline')}>
        <Button variant="secondary" onClick={() => void downloadFile(`/exports?format=json&locale=${locale}`, 'toxofollow.json')}>
          {locale === 'fr' ? 'Télécharger le dossier (JSON)' : 'Download record (JSON)'}
        </Button>
        <p className="text-xs text-slate-400 mt-3">
          {locale === 'fr'
            ? 'Le PDF de consultation complet est généré dans l’application mobile (hors ligne).'
            : 'The full consultation PDF is generated in the mobile app (offline).'}
        </p>
      </Card>

      <Card>
        <p className="text-xs text-slate-500">{t('disclaimer.firstLaunch')}</p>
      </Card>
    </div>
  );
}
